import { isObject, KeyValue } from './utils';

const MAX_MESSAGE_LENGTH = 256;

export type SafeFailureClassification = {
  errorName: string;
  reason: string;
};

const getString = (value: unknown, key: string): string | undefined => {
  if (!isObject(value) || typeof value[key] !== 'string') return undefined;
  return (value[key] as string).slice(0, MAX_MESSAGE_LENGTH);
};

const getErrorInfo = (value: KeyValue | undefined): KeyValue | undefined => {
  if (isObject(value?.ErrorInfo)) return value.ErrorInfo;
  return isObject(value?.errorInfo) ? value.errorInfo : undefined;
};

const getResponseMessage = (responseJson: unknown): string | undefined => {
  if (!isObject(responseJson)) return undefined;

  const body = isObject(responseJson.body) ? responseJson.body : undefined;
  const rootError = isObject(responseJson.error)
    ? responseJson.error
    : undefined;
  const bodyError = isObject(body?.error) ? body.error : undefined;
  const rootErrors = Array.isArray(responseJson.errors)
    ? responseJson.errors
    : [];
  const bodyErrors = Array.isArray(body?.errors) ? body.errors : [];
  const validationErrors = Array.isArray(rootError?.validationErrors)
    ? rootError.validationErrors
    : Array.isArray(bodyError?.validationErrors)
    ? bodyError.validationErrors
    : [];

  return [
    getString(rootError, 'message'),
    getString(bodyError, 'message'),
    getString(getErrorInfo(responseJson), 'errorMessage'),
    getString(getErrorInfo(body), 'errorMessage'),
    getString(rootErrors[0], 'message'),
    getString(bodyErrors[0], 'message'),
    getString(validationErrors[0], 'message'),
    getString(body, 'message'),
    getString(responseJson, 'message'),
    getString(responseJson, 'fault')
  ].find(Boolean);
};

const SAFE_FAILURE_RULES: Array<[RegExp, SafeFailureClassification]> = [
  [
    /number list not contains the number requested|number (?:is )?no longer available|number.*already (?:reserved|selected)/i,
    {
      reason: 'selectedNumberUnavailable',
      errorName: 'That number is no longer available'
    }
  ],
  [/invalid[^a-z0-9]*otp/i, { reason: 'invalidOtp', errorName: 'Invalid OTP' }],
  [
    /out[^a-z0-9]*of[^a-z0-9]*stock|item.*not available/i,
    { reason: 'itemOutOfStock', errorName: 'Item out of stock' }
  ],
  [
    /token.*expected|token.*required|unauthoriz|authentication.*required/i,
    {
      reason: 'authenticationRequired',
      errorName: 'Authentication required'
    }
  ],
  [
    /validation.*fail|invalid.*parameter|missing.*parameter|required/i,
    {
      reason: 'requestValidationFailed',
      errorName: 'Request validation failed'
    }
  ],
  [
    /not eligible/i,
    { reason: 'customerNotEligible', errorName: 'Customer not eligible' }
  ],
  [
    /not found/i,
    {
      reason: 'requestedInformationNotFound',
      errorName: 'Requested information not found'
    }
  ],
  [
    /invalid.*file.*format/i,
    { reason: 'invalidFileFormat', errorName: 'Invalid file format' }
  ],
  [/logout.*fail/i, { reason: 'logoutFailed', errorName: 'Logout failed' }],
  [
    /failed.*fetch|unable.*fetch|unable.*retrieve/i,
    {
      reason: 'unableToRetrieveInformation',
      errorName: 'Unable to retrieve information'
    }
  ],
  [
    /internal.*server|bad.*gateway|service.*unavailable|time.?out/i,
    {
      reason: 'serviceUnavailable',
      errorName: 'Service temporarily unavailable'
    }
  ]
];

export const getKnownFailureClassification = (
  responseJson: unknown
): SafeFailureClassification | undefined => {
  const message = getResponseMessage(responseJson);
  if (!message) return undefined;

  return getKnownFailureClassificationFromText(message);
};

export const getKnownFailureClassificationFromText = (
  value: string
): SafeFailureClassification | undefined =>
  SAFE_FAILURE_RULES.find(([pattern]) => pattern.test(value))?.[1];
